import React, { useEffect, useMemo, useState } from "react";
import { Plus, X, Trash2, Save, Search, Filter, Grid, List as ListIcon, MessageSquarePlus, Calendar as CalendarIcon, Video, ExternalLink, Loader2, Upload } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STAGES = [
  { id: "new", label: "Nuovo" },
  { id: "contacted", label: "Contattato" },
  { id: "appointment", label: "Appuntamento" },
  { id: "negotiation", label: "Trattativa" },
  { id: "contract_sent", label: "Contratto inviato" },
  { id: "won", label: "Vinto" },
  { id: "lost", label: "Perso" },
];
const STAGE_LABEL = Object.fromEntries(STAGES.map((s) => [s.id, s.label]));

const PAYMENT_STATUS = [
  { id: "not_paid", label: "Non pagato" },
  { id: "down_payment", label: "Acconto" },
  { id: "paid", label: "Saldato" },
];
const PAYMENT_METHOD = [
  { id: "bank_transfer", label: "Bonifico" },
  { id: "paypal", label: "PayPal" },
  { id: "stripe", label: "Stripe" },
  { id: "cash", label: "Contanti" },
  { id: "other", label: "Altro" },
];

export default function CRMLeads() {
  const { user } = useAuth();
  const isReadOnlySeller = user?.role === "seller";
  const canCreate = ["admin", "outreach"].includes(user?.role);

  const [leads, setLeads] = useState([]);
  const [packages, setPackages] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [view, setView] = useState("kanban");
  const [filter, setFilter] = useState({ status: "", source: "", seller_id: "", q: "" });
  const [detail, setDetail] = useState(null);
  const [newLead, setNewLead] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v) params.set(k, v); });
    const [l, p, u] = await Promise.all([
      api.get(`/crm/leads?${params.toString()}`),
      api.get("/crm/packages"),
      user?.role === "admin" ? api.get("/crm/users?role=seller") : Promise.resolve({ data: [] }),
    ]);
    setLeads(l.data);
    setPackages(p.data);
    setSellers(u.data);
    setLoading(false);
  };
  useEffect(() => { load(); // eslint-disable-line react-hooks/exhaustive-deps
  }, [filter.status, filter.source, filter.seller_id, filter.q]);

  const groupedByStage = useMemo(() => {
    const g = {};
    STAGES.forEach((s) => { g[s.id] = []; });
    leads.forEach((l) => { (g[l.status || "new"] ||= []).push(l); });
    return g;
  }, [leads]);

  const sellerName = (id) => sellers.find((s) => s.id === id)?.name || sellers.find((s) => s.id === id)?.email || "—";

  // Drag & Drop: change stage on drop
  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, source, destination } = result;
    if (source.droppableId === destination.droppableId) return;
    const newStatus = destination.droppableId;
    // Optimistic update
    setLeads((prev) => prev.map((l) => l.id === draggableId ? { ...l, status: newStatus } : l));
    try {
      await api.patch(`/crm/leads/${draggableId}`, { status: newStatus });
      toast.success(`Lead spostato in "${STAGES.find(s => s.id === newStatus)?.label}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nello spostamento");
      load();  // rollback via reload
    }
  };

  return (
    <div className="p-6 lg:p-10" data-testid="crm-leads-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— CRM</div>
          <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Lead</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setView(view === "kanban" ? "list" : "kanban")} className="btn-outline-dark py-2 px-4 text-xs" data-testid="crm-toggle-view">
            {view === "kanban" ? <><ListIcon size={14} /> Lista</> : <><Grid size={14} /> Kanban</>}
          </button>
          {canCreate && (
            <>
              <button onClick={() => setShowImport(true)} className="btn-outline-dark py-2 px-4 text-xs" data-testid="crm-import-csv-btn">
                <Upload size={14} /> Importa CSV
              </button>
              <button onClick={() => setNewLead({ name: "", email: "", phone: "", artist_name: "", genre: "", instagram: "", region: "", source_page: "outreach" })} className="btn-cta py-2 px-4 text-xs" data-testid="crm-new-lead-btn">
                <Plus size={14} /> Nuovo lead
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 flex gap-2 flex-wrap items-center">
        <div className="flex items-center gap-2 border-2 border-[#0A0A0A] px-3 py-2 bg-white">
          <Search size={14} />
          <input value={filter.q} onChange={(e) => setFilter((f) => ({ ...f, q: e.target.value }))} placeholder="Cerca nome, email, artista…" className="bg-transparent focus:outline-none text-sm w-64" data-testid="crm-search" />
        </div>
        <select value={filter.status} onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))} className="border-2 border-[#0A0A0A] px-3 py-2 bg-white text-sm" data-testid="crm-filter-status">
          <option value="">Tutti stati</option>
          {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <select value={filter.source} onChange={(e) => setFilter((f) => ({ ...f, source: e.target.value }))} className="border-2 border-[#0A0A0A] px-3 py-2 bg-white text-sm" data-testid="crm-filter-source">
          <option value="">Tutte fonti</option>
          <option value="landing-1">Landing 1</option>
          <option value="landing-2">Landing 2</option>
          <option value="contact">Contatti</option>
          <option value="outreach">Outreach</option>
        </select>
        {user?.role === "admin" && (
          <select value={filter.seller_id} onChange={(e) => setFilter((f) => ({ ...f, seller_id: e.target.value }))} className="border-2 border-[#0A0A0A] px-3 py-2 bg-white text-sm" data-testid="crm-filter-seller">
            <option value="">Tutti venditori</option>
            {sellers.map((s) => <option key={s.id} value={s.id}>{s.name || s.email}</option>)}
          </select>
        )}
      </div>

      {loading ? <div className="text-[#0A0A0A]/50">Caricamento…</div> : leads.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-12 text-center text-[#0A0A0A]/50" data-testid="crm-leads-empty">
          Nessun lead trovato con questi filtri.
        </div>
      ) : view === "kanban" ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 overflow-x-auto" data-testid="crm-kanban">
            {STAGES.map((stage) => (
              <div key={stage.id} className="min-w-[220px]">
                <div className="border-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7] px-3 py-2 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest font-bold">{stage.label}</span>
                  <span className="text-xs bg-[#FF5A1F] text-[#FAFAF7] px-2 py-0.5">{groupedByStage[stage.id]?.length || 0}</span>
                </div>
                <Droppable droppableId={stage.id}>
                  {(dropProvided, dropSnapshot) => (
                    <div
                      ref={dropProvided.innerRef}
                      {...dropProvided.droppableProps}
                      className={`border-x-2 border-b-2 border-[#0A0A0A] p-2 min-h-[100px] space-y-2 transition-colors ${dropSnapshot.isDraggingOver ? "bg-[#FF5A1F]/10" : "bg-[#F2F2EC]"}`}
                      data-testid={`kanban-col-${stage.id}`}
                    >
                      {(groupedByStage[stage.id] || []).map((l, idx) => (
                        <Draggable key={l.id} draggableId={l.id} index={idx}>
                          {(dragProvided, dragSnapshot) => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}
                              onClick={() => !dragSnapshot.isDragging && setDetail(l)}
                              className={`bg-[#FAFAF7] border p-3 cursor-grab active:cursor-grabbing transition ${dragSnapshot.isDragging ? "shadow-2xl border-[#FF5A1F] rotate-1" : "border-[#0A0A0A] hover:bg-white"}`}
                              data-testid={`lead-card-${l.id}`}
                            >
                              <div className="font-bold text-sm truncate">{l.name}</div>
                              {l.artist_name && <div className="text-[10px] uppercase text-[#FF5A1F] truncate">{l.artist_name}</div>}
                              <div className="text-[10px] text-[#0A0A0A]/60 truncate mt-1">{l.email}</div>
                              {(l.utm_source || l.utm_platform || l.utm_campaign) && (
                                <div className="mt-1.5 flex flex-wrap gap-1">
                                  {l.utm_platform && <span className="text-[8px] uppercase tracking-wider bg-[#0A0A0A] text-[#FAFAF7] px-1.5 py-0.5 font-bold">{l.utm_platform}</span>}
                                  {l.utm_source && !l.utm_platform && <span className="text-[8px] uppercase tracking-wider bg-[#0A0A0A] text-[#FAFAF7] px-1.5 py-0.5 font-bold">{l.utm_source}</span>}
                                  {l.utm_campaign && <span className="text-[8px] uppercase tracking-wider border border-[#FF5A1F] text-[#FF5A1F] px-1.5 py-0.5 font-bold truncate max-w-[110px]">{l.utm_campaign}</span>}
                                </div>
                              )}
                              <div className="mt-2 flex items-center justify-between text-[9px] uppercase tracking-widest text-[#0A0A0A]/60">
                                <span>{l.source_page}</span>
                                {l.packages_total > 0 && <span className="text-[#FF5A1F] font-bold">€{l.packages_total}</span>}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {dropProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      ) : (
        <div className="border-2 border-[#0A0A0A] overflow-x-auto bg-[#FAFAF7]" data-testid="crm-list">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
              <tr>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Data</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Nome</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Email</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Stato</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Fonte</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Venditore</th>
                <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Totale</th>
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} onClick={() => setDetail(l)} className="border-t border-[#0A0A0A]/10 hover:bg-[#F2F2EC] cursor-pointer" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 text-xs">{new Date(l.created_at).toLocaleDateString("it-IT")}</td>
                  <td className="px-4 py-3 font-bold">{l.name} {l.artist_name && <span className="text-[10px] uppercase text-[#FF5A1F] ml-1">({l.artist_name})</span>}</td>
                  <td className="px-4 py-3 text-[#0A0A0A]/80">{l.email}</td>
                  <td className="px-4 py-3"><span className="text-[10px] uppercase font-bold bg-[#0A0A0A] text-[#FAFAF7] px-2 py-0.5">{STAGE_LABEL[l.status] || l.status}</span></td>
                  <td className="px-4 py-3 text-xs">{l.source_page}</td>
                  <td className="px-4 py-3 text-xs">{sellerName(l.assigned_seller_id)}</td>
                  <td className="px-4 py-3 font-bold text-[#FF5A1F]">{l.packages_total ? `€${l.packages_total}` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {detail && (
        <LeadDetail
          lead={detail}
          packages={packages}
          sellers={sellers}
          userRole={user?.role}
          onClose={() => setDetail(null)}
          onSaved={() => { setDetail(null); load(); }}
        />
      )}

      {newLead && canCreate && (
        <NewLeadModal
          data={newLead}
          onChange={setNewLead}
          sellers={sellers}
          userRole={user?.role}
          onClose={() => setNewLead(null)}
          onSaved={() => { setNewLead(null); load(); }}
        />
      )}

      {showImport && canCreate && (
        <ImportCsvModal
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------- Lead Detail modal ----------
const LeadDetail = ({ lead, packages, sellers, userRole, onClose, onSaved }) => {
  const [data, setData] = useState({
    status: lead.status || "new",
    packages_ids: lead.packages_ids || [],
    payment_status: lead.payment_status || "not_paid",
    payment_method: lead.payment_method || "",
    assigned_seller_id: lead.assigned_seller_id || "",
  });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/crm/leads/${lead.id}/notes`).then((r) => setNotes(r.data)).catch(() => {});
  }, [lead.id]);

  const total = useMemo(() =>
    (data.packages_ids || []).reduce((sum, id) => sum + (packages.find((p) => p.id === id)?.price || 0), 0)
  , [data.packages_ids, packages]);

  const togglePkg = (id) => {
    setData((d) => ({
      ...d,
      packages_ids: d.packages_ids.includes(id) ? d.packages_ids.filter((x) => x !== id) : [...d.packages_ids, id],
    }));
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/crm/leads/${lead.id}`, data);
      toast.success("Lead aggiornato");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await api.post(`/crm/leads/${lead.id}/notes`, { text: newNote.trim() });
      const r = await api.get(`/crm/leads/${lead.id}/notes`);
      setNotes(r.data);
      setNewNote("");
      toast.success("Nota salvata");
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="lead-detail-modal">
      <div className="bg-[#FAFAF7] max-w-3xl w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F]">{lead.source_page}</div>
            <h3 className="font-display text-2xl uppercase">{lead.name}</h3>
            {lead.artist_name && <div className="text-xs uppercase text-[#FAFAF7]/60">{lead.artist_name}</div>}
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div className="space-y-3 text-sm">
            <Info label="Email" value={lead.email} />
            <Info label="Telefono" value={lead.phone} />
            <Info label="Genere" value={lead.genre} />
            <Info label="Instagram" value={lead.instagram} />
            <Info label="Ascoltatori" value={lead.monthly_listeners} />
            <Info label="Regione" value={lead.region} />
            <Info label="Obiettivo" value={lead.goal} />
            <Info label="Budget" value={lead.budget} />
            {lead.message && <div><span className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 font-bold">Messaggio</span><div className="mt-1 border border-[#0A0A0A]/20 p-2 bg-white">{lead.message}</div></div>}
            <div className="pt-3 border-t-2 border-[#0A0A0A]/10">
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">Attribution / UTM Tracking</div>
              {(!lead.utm_source && !lead.utm_campaign && !lead.utm_platform && !lead.utm_medium && !lead.utm_ad && !lead.utm_adset) ? (
                <div className="text-xs text-[#0A0A0A]/50 italic bg-[#0A0A0A]/5 p-2 border border-[#0A0A0A]/10">
                  Nessun parametro UTM tracciato — lead arrivato senza campagna o traffico diretto.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {[
                    { key: "utm_platform", label: "Piattaforma", accent: true },
                    { key: "utm_source", label: "Fonte", accent: true },
                    { key: "utm_campaign", label: "Campagna" },
                    { key: "utm_medium", label: "Mezzo" },
                    { key: "utm_ad", label: "Ad" },
                    { key: "utm_adset", label: "Ad set" },
                    { key: "utm_content", label: "Content" },
                    { key: "utm_term", label: "Term" },
                  ].filter((f) => lead[f.key]).map((f) => (
                    <div key={f.key} className="flex items-center justify-between text-xs border-b border-[#0A0A0A]/10 pb-1">
                      <span className="text-[#0A0A0A]/50 uppercase tracking-widest text-[10px]">{f.label}</span>
                      <span className={`font-mono text-xs px-2 py-0.5 ${f.accent ? "bg-[#FF5A1F] text-[#FAFAF7] font-bold" : "bg-[#0A0A0A]/5"}`}>{lead[f.key]}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <Select label="Stato pipeline" value={data.status} options={STAGES.map(s => ({value:s.id, label:s.label}))} onChange={(v) => setData((d) => ({ ...d, status: v }))} testId="detail-status" />
            {userRole === "admin" && (
              <Select label="Venditore assegnato" value={data.assigned_seller_id} options={[{value:"", label:"— Nessuno —"}, ...sellers.map(s => ({value:s.id, label:s.name || s.email}))]} onChange={(v) => setData((d) => ({ ...d, assigned_seller_id: v }))} testId="detail-seller" />
            )}
            <div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Pacchetti (componibili)</span>
              <div className="mt-2 space-y-1 border-2 border-[#0A0A0A] p-3 bg-white">
                {packages.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={data.packages_ids.includes(p.id)} onChange={() => togglePkg(p.id)} data-testid={`pkg-check-${p.id}`} />
                    <span className="flex-1">{p.name}</span>
                    <span className="text-xs font-bold text-[#FF5A1F]">€{p.price}</span>
                  </label>
                ))}
                <div className="pt-2 mt-2 border-t border-[#0A0A0A]/20 flex items-center justify-between font-display text-lg">
                  <span className="uppercase text-xs">Totale</span>
                  <span className="text-[#FF5A1F]">€{total.toLocaleString('it-IT')}</span>
                </div>
              </div>
            </div>
            <Select label="Stato pagamento" value={data.payment_status} options={PAYMENT_STATUS.map(p => ({value:p.id, label:p.label}))} onChange={(v) => setData((d) => ({ ...d, payment_status: v }))} testId="detail-payment-status" />
            <Select label="Metodo pagamento" value={data.payment_method} options={[{value:"", label:"— Non specificato —"}, ...PAYMENT_METHOD.map(p => ({value:p.id, label:p.label}))]} onChange={(v) => setData((d) => ({ ...d, payment_method: v }))} testId="detail-payment-method" />
          </div>
        </div>

        {/* Google Calendar appointment scheduler */}
        {(lead.calendar_event_link || data.status === "appointment") && (
          <div className="p-6 border-t-2 border-[#0A0A0A]/10">
            <AppointmentSection lead={lead} />
          </div>
        )}

        {/* Notes */}
        <div className="p-6 border-t-2 border-[#0A0A0A]/10">
          <h4 className="font-display text-xl uppercase mb-3">Note e storico</h4>
          <div className="flex gap-2 mb-3">
            <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Aggiungi una nota…" className="flex-1 border-2 border-[#0A0A0A] px-3 py-2 bg-white text-sm" data-testid="note-input" />
            <button onClick={addNote} className="btn-cta py-2 px-4 text-xs" data-testid="note-add-btn"><MessageSquarePlus size={14} /> Aggiungi</button>
          </div>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notes.map((n) => (
              <div key={n.id} className={`border-l-2 px-3 py-2 bg-white ${n.kind === "status_change" ? "border-[#FF5A1F]" : "border-[#0A0A0A]"}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 flex justify-between">
                  <span>{n.author_name}</span>
                  <span>{new Date(n.created_at).toLocaleString("it-IT")}</span>
                </div>
                <div className="text-sm mt-1">{n.text}</div>
              </div>
            ))}
            {notes.length === 0 && <div className="text-xs text-[#0A0A0A]/40 italic">Nessuna nota ancora.</div>}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-dark" data-testid="detail-cancel">Chiudi</button>
          <button onClick={save} disabled={busy} className="btn-cta disabled:opacity-60" data-testid="detail-save">
            <Save size={14} /> {busy ? "..." : "Salva"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</div>
    <div className="text-sm">{value || "—"}</div>
  </div>
);

const Select = ({ label, value, options, onChange, testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm">
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

// ---------- New Lead modal ----------
const NewLeadModal = ({ data, onChange, sellers, userRole, onClose, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const set = (k, v) => onChange({ ...data, [k]: v });
  const save = async () => {
    if (!data.name) { toast.error("Nome obbligatorio"); return; }
    setBusy(true);
    try {
      await api.post("/crm/leads", data);
      toast.success("Lead creato");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="new-lead-modal">
      <div className="bg-[#FAFAF7] max-w-lg w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex justify-between">
          <h3 className="font-display text-2xl uppercase">Nuovo lead</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <Field label="Nome *" value={data.name} onChange={(v) => set("name", v)} testId="new-name" />
          <Field label="Email" type="email" value={data.email} onChange={(v) => set("email", v)} testId="new-email" />
          <Field label="Telefono" value={data.phone} onChange={(v) => set("phone", v)} testId="new-phone" />
          <Field label="Nome d'arte" value={data.artist_name} onChange={(v) => set("artist_name", v)} testId="new-artist" />
          <Field label="Genere" value={data.genre} onChange={(v) => set("genre", v)} testId="new-genre" />
          <Field label="Instagram" value={data.instagram} onChange={(v) => set("instagram", v)} testId="new-ig" />
          <Field label="Regione" value={data.region} onChange={(v) => set("region", v)} testId="new-region" />
          {userRole === "admin" && (
            <Select label="Assegna a venditore (o auto)" value={data.assigned_seller_id || ""} options={[{value:"", label:"— Auto (round-robin) —"}, ...sellers.map(s => ({value:s.id, label:s.name || s.email}))]} onChange={(v) => set("assigned_seller_id", v)} testId="new-assign" />
          )}
        </div>
        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          <button onClick={onClose} className="btn-outline-dark" data-testid="new-cancel">Annulla</button>
          <button onClick={save} disabled={busy} className="btn-cta disabled:opacity-60" data-testid="new-save">
            <Save size={14} /> {busy ? "..." : "Crea"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = "text", testId }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" />
  </label>
);


// ---------- Appointment (Google Calendar) section inside LeadDetail ----------
const AppointmentSection = ({ lead }) => {
  const [status, setStatus] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const now = new Date();
  const initialStart = new Date(now.getTime() + 24 * 60 * 60 * 1000); // tomorrow same time
  initialStart.setMinutes(0, 0, 0);
  const initialEnd = new Date(initialStart.getTime() + 30 * 60 * 1000);

  const toLocalInput = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [start, setStart] = useState(toLocalInput(initialStart));
  const [end, setEnd] = useState(toLocalInput(initialEnd));
  const [title, setTitle] = useState(`Call Perla Music — ${lead.name}`);
  const [withMeet, setWithMeet] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/crm/google/config-status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ configured: false, connected: false }))
      .finally(() => setLoadingStatus(false));
  }, []);

  const create = async () => {
    setBusy(true);
    try {
      // Convert local datetime-input to ISO (assume Europe/Rome local time — backend re-tags it)
      const startIso = new Date(start).toISOString();
      const endIso = new Date(end).toISOString();
      const r = await api.post("/crm/google/events", {
        lead_id: lead.id,
        title,
        start: startIso,
        end: endIso,
        description: `Lead: ${lead.name}${lead.artist_name ? " (" + lead.artist_name + ")" : ""}\nEmail: ${lead.email || "—"}\nTelefono: ${lead.phone || "—"}`,
        with_meet: withMeet,
      });
      toast.success("Appuntamento creato su Google Calendar");
      lead.calendar_event_link = r.data.htmlLink;
      lead.calendar_meet_link = r.data.meet_link;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione evento");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="lead-appointment-section">
      <h4 className="font-display text-xl uppercase mb-3 flex items-center gap-2">
        <CalendarIcon size={18} className="text-[#FF5A1F]" /> Appuntamento
      </h4>

      {lead.calendar_event_link ? (
        <div className="border-2 border-emerald-600/40 bg-emerald-50 p-4">
          <div className="text-[10px] uppercase tracking-widest text-emerald-900 font-bold">Appuntamento fissato</div>
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            <a href={lead.calendar_event_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#0A0A0A] hover:text-[#FF5A1F]" data-testid="lead-event-link">
              Apri su Google Calendar <ExternalLink size={13} />
            </a>
            {lead.calendar_meet_link && (
              <a href={lead.calendar_meet_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-bold text-[#FF5A1F] hover:underline" data-testid="lead-meet-link">
                <Video size={13} /> Google Meet
              </a>
            )}
          </div>
        </div>
      ) : loadingStatus ? (
        <div className="flex items-center gap-2 text-sm text-[#0A0A0A]/50"><Loader2 size={14} className="animate-spin" /> Verifica connessione…</div>
      ) : !status?.configured ? (
        <div className="text-sm text-[#0A0A0A]/60 border-2 border-amber-500/30 bg-amber-50 p-3">
          Google Calendar non ancora configurato dal backend. Vai su <a href="/admin/credenziali-crm" className="text-[#FF5A1F] font-bold">Credenziali CRM</a> per i dettagli.
        </div>
      ) : !status?.connected ? (
        <div className="text-sm text-[#0A0A0A]/70 border-2 border-[#FF5A1F]/30 bg-[#FF5A1F]/5 p-3">
          Prima devi collegare il tuo Google Calendar. Vai su <a href="/crm/impostazioni" className="text-[#FF5A1F] font-bold">Impostazioni</a>.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block md:col-span-2">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Titolo evento</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="appt-title" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Inizio</span>
            <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="appt-start" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Fine</span>
            <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 block w-full border-2 border-[#0A0A0A] py-2 px-3 bg-white text-sm" data-testid="appt-end" />
          </label>
          <label className="flex items-center gap-2 md:col-span-2 text-sm">
            <input type="checkbox" checked={withMeet} onChange={(e) => setWithMeet(e.target.checked)} data-testid="appt-with-meet" />
            <span>Aggiungi link Google Meet automaticamente</span>
          </label>
          <button onClick={create} disabled={busy} className="btn-cta md:col-span-2 disabled:opacity-60" data-testid="appt-create-btn">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarIcon size={14} />} {busy ? "Creazione…" : "Crea evento su Google Calendar"}
          </button>
        </div>
      )}
    </div>
  );
};

// ---------- Import CSV Modal ----------
const ImportCsvModal = ({ onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [autoAssign, setAutoAssign] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState([]);

  const handleFile = async (f) => {
    setFile(f);
    setResult(null);
    if (!f) { setPreview([]); return; }
    const text = await f.text();
    const lines = text.split(/\r?\n/).filter(Boolean).slice(0, 6);
    setPreview(lines);
  };

  const upload = async () => {
    if (!file) { toast.error("Seleziona un file CSV"); return; }
    setBusy(true);
    try {
      const text = await file.text();
      const r = await api.post(
        `/crm/leads/import?auto_assign=${autoAssign ? "1" : "0"}`,
        text,
        { headers: { "Content-Type": "text/csv" } },
      );
      setResult(r.data);
      toast.success(`Importati ${r.data.imported} lead`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nell'import");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="import-csv-modal">
      <div className="bg-[#FAFAF7] max-w-2xl w-full max-h-[92vh] overflow-y-auto border-2 border-[#0A0A0A]" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0A0A0A] text-[#FAFAF7] p-4 flex justify-between z-10">
          <h3 className="font-display text-2xl uppercase flex items-center gap-2"><Upload size={20} /> Importa lead da CSV</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="text-sm text-[#0A0A0A]/70 leading-relaxed">
            Il CSV deve avere una riga di header. Colonne riconosciute (case‑insensitive):
            <div className="mt-2 flex flex-wrap gap-1">
              {["name", "email", "phone", "artist_name", "genre", "instagram", "region", "monthly_listeners", "goal", "budget", "message", "source_page", "utm_source", "utm_campaign", "utm_medium", "utm_platform", "utm_ad", "utm_adset"].map((c) => (
                <code key={c} className="text-[10px] bg-[#0A0A0A]/10 px-1.5 py-0.5">{c}</code>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#0A0A0A]/60">Solo <code className="bg-[#0A0A0A]/10 px-1">name</code> è obbligatoria. Dedup automatica su email/telefono.</p>
          </div>

          {!result && (
            <>
              <label className="block border-2 border-dashed border-[#0A0A0A]/30 p-6 text-center cursor-pointer hover:border-[#FF5A1F] transition-colors">
                <input type="file" accept=".csv,text/csv" onChange={(e) => handleFile(e.target.files?.[0])} className="hidden" data-testid="import-csv-input" />
                <Upload size={28} className="mx-auto text-[#FF5A1F]" />
                <div className="mt-2 font-bold">{file ? file.name : "Scegli un file CSV"}</div>
                <div className="text-xs text-[#0A0A0A]/50 mt-1">{file ? `${(file.size / 1024).toFixed(1)} KB` : "Trascina o clicca per selezionare"}</div>
              </label>

              {preview.length > 0 && (
                <div className="border-2 border-[#0A0A0A]/10 bg-white p-3 text-[11px] font-mono max-h-40 overflow-auto" data-testid="import-csv-preview">
                  <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mb-1 font-bold font-sans">Anteprima (prime 6 righe)</div>
                  {preview.map((line, i) => (
                    <div key={i} className={`truncate ${i === 0 ? "font-bold text-[#FF5A1F]" : ""}`}>{line}</div>
                  ))}
                </div>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={autoAssign} onChange={(e) => setAutoAssign(e.target.checked)} data-testid="import-auto-assign" />
                <span>Assegna automaticamente ai venditori (round‑robin)</span>
              </label>
            </>
          )}

          {result && (
            <div className="border-2 border-emerald-600/40 bg-emerald-50 p-4" data-testid="import-csv-result">
              <div className="font-bold text-emerald-900">Import completato</div>
              <div className="mt-2 grid grid-cols-3 gap-3 text-center">
                <div>
                  <div className="font-display text-3xl text-emerald-700">{result.imported}</div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-800">Importati</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-amber-700">{result.skipped}</div>
                  <div className="text-[10px] uppercase tracking-widest text-amber-800">Scartati</div>
                </div>
                <div>
                  <div className="font-display text-3xl text-[#0A0A0A]/50">{(result.duplicates || []).length}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">Duplicati</div>
                </div>
              </div>
              {result.duplicates?.length > 0 && (
                <div className="mt-3 text-xs text-[#0A0A0A]/70">
                  <div className="uppercase tracking-widest text-[10px] font-bold mb-1">Prime email duplicate:</div>
                  <code className="block bg-white p-2 border max-h-24 overflow-auto">{result.duplicates.join(", ")}</code>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="sticky bottom-0 bg-[#FAFAF7] border-t-2 border-[#0A0A0A] p-4 flex justify-end gap-3">
          {result ? (
            <button onClick={onDone} className="btn-cta" data-testid="import-csv-done">Chiudi & aggiorna lista</button>
          ) : (
            <>
              <button onClick={onClose} className="btn-outline-dark">Annulla</button>
              <button onClick={upload} disabled={busy || !file} className="btn-cta disabled:opacity-60" data-testid="import-csv-upload">
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {busy ? "Import…" : "Importa"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

